import { SelectTreeViewProps } from 'components/shared'
import { TreeViewDropdownLineProps } from 'components/shared/SelectTreeView/TreeViewDropdownLine'
import { action, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { BrandDTO, ProductDTO, ProjectDTO, ViewerEventEnums } from 'models'
import { BRANDS_MAIN_STORE } from 'store/BrandsMainStore'
import { TriggerSearchFunc } from 'store/PaginationSearchBase/PaginationSearchBase.types'
import { PRODUCTS_MAIN_STORE } from 'store/ProductsMainStore'
import { PROJECT_MAIN_STORE } from 'store/ProjectMainStore'
import { RootStore } from 'store/RootStore'
import {
  OverviewStatTabs,
  ProductProjectStat,
  STATISTICS_OVERVIEW_STORE,
  statsOverviewExpensesEvents,
  statsOverviewInteractionEvents
} from 'store/StatisticsOverviewStore'
import { PieSegment, STATISTICS_PAGE_STORE, statsColorPallette } from 'store/StatisticsPageStore'
import { getMainImageSrcForObj, getSortingResult, getToFixed2Number } from 'utilities'
import {
  StatsOverviewTableEntity,
  statsOverviewTableInitialPaginationProps,
  StatsOverviewTableSortProps
} from './StatisticsOverviewTableStore.types'
import {
  DraggableColLabels,
  FilterOptionSettings,
  getCorrespondingEntityIdKey, hasMatchingChild,
  SelectedCorrespondingEntitiesMap,
  SelectedCorrespondingEntitySets,
  StatsBaseTableStore,
  StatsTableColType,
  StatsTableEntityObject
} from '../StatsBaseTableStore'

export const STATISTICS_OVERVIEW_TABLE_STORE = 'STATISTICS_OVERVIEW_TABLE_STORE'

export class StatisticsOverviewTableStore extends StatsBaseTableStore<
  StatsOverviewTableSortProps,
  StatsOverviewTableEntity
> {
  private updatePeriodChartTimeout: NodeJS.Timeout = null

  constructor (rootStore: RootStore) {
    super(
      rootStore,
      statsOverviewTableInitialPaginationProps
    )
  }

  @observable isExpandedBlobsColumn: boolean = false;
  @observable isExpandedProductPagesColumn: boolean = false;
  @observable isExpandedPostersColumn: boolean = false;

  @observable private selectedViewerEventsToShow: Set<ViewerEventEnums> =
    new Set(statsOverviewExpensesEvents)

  @action private setSelectedViewerEventsToShow = (events: ViewerEventEnums[]) => {
    this.selectedViewerEventsToShow = new Set(events)
  }

  @action public setIsExpandedBlobsColumn = (isExpanded: boolean) => {
    this.isExpandedBlobsColumn = isExpanded;
  }

  @action public setIsExpandedProductPagesColumn = (isExpanded: boolean) => {
    this.isExpandedProductPagesColumn = isExpanded;
  }

  @action public setIsExpandedPostersColumn = (isExpanded: boolean) => {
    this.isExpandedPostersColumn = isExpanded;
  }

  @action onMainEntityCheckboxClick = (entityId: number) => {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        deleteEntityTotalPeriodData,
        hasBest,
        getBestProducts
      }
    } = this.rootStore

    if (this.isMainEntitySelected(entityId)) {
      this.selectedMainEntities.delete(entityId)
      deleteEntityTotalPeriodData(entityId)
      return
    }
    if (this.selectedMainEntities.size >= 6) {
      this.selectedMainEntities.forEach((_, entityId) => {
        deleteEntityTotalPeriodData(entityId)
      })
      this.setSelectedMainEntities(new Map([[entityId, statsColorPallette[0].fill]]))
    } else {
      const usedColors = [...this.selectedMainEntities.values()]
      for (const color of statsColorPallette) {
        if (!usedColors.includes(color.fill)) {
          this.selectedMainEntities.set(entityId, color.fill)
          break
        }
      }
    }

    this.triggerPeriodChartUpdate(entityId)
    if (this.isMainEntityProduct && !hasBest) {
      getBestProducts()
    }
  }

  @action private setSelectedCorrespondingEntitiesMap = (
    map: SelectedCorrespondingEntitiesMap
  ): void => {
    this.selectedCorrespondingEntitiesMap = map
  }

  @action private setSelectedCorrespondingEntities = (
    mainEntityId: number,
    sets: SelectedCorrespondingEntitySets
  ): void => {
    this.selectedCorrespondingEntitiesMap.set(mainEntityId, sets)
  }

  @action private switchViewerEventShown = (eventType: ViewerEventEnums): void => {
    if (this.isViewerEventShown(eventType)) {
      this.selectedViewerEventsToShow.delete(eventType)
    } else {
      this.selectedViewerEventsToShow.add(eventType)
    }
  }

  private getInitialCorrespondingEntitySetsForMainEntity = (data: {
    mainEntityId?: number,
    tableEntity?: StatsOverviewTableEntity
  }): SelectedCorrespondingEntitySets => {
    const tableEntity = data.tableEntity ||
      this.searchResults.find(r => r.mainEntity.id === this.getParentId(data.mainEntityId))
    if (!tableEntity) return null

    const mainEntityId = tableEntity.mainEntity.id

    const correspondingEntitySets: SelectedCorrespondingEntitySets = {
      [DraggableColLabels.PROJECT]: this.isMainEntityProject
        ? this.rootStore[PROJECT_MAIN_STORE].getProjectChildrenIds(mainEntityId)
        : new Set(),
      // [DraggableColLabels.PROJECT]: new Set(),
      [DraggableColLabels.STORE]: new Set(),
      [DraggableColLabels.PRODUCTS]: new Set()
    }

    const {
      primaryCorrespondingEntityColLabel,
      secondaryCorrespondingEntityColLabel
    } = this.tableSettings

    correspondingEntitySets[primaryCorrespondingEntityColLabel] =
      new Set(tableEntity.primaryCorrespondingEntities.map(e => e.id))

    if (secondaryCorrespondingEntityColLabel) {
      correspondingEntitySets[secondaryCorrespondingEntityColLabel] =
        new Set(tableEntity.secondaryCorrespondingEntities.map(e => e?.id))
    }

    return correspondingEntitySets
  }

  private updateSelectedCorrespondingEntities = (
    tableEntities: StatsOverviewTableEntity[]
  ): void => {
    const isMainEntityColChanged = this.selectedCorrespondingEntitiesMap.size === 0
    if (!isMainEntityColChanged) return

    const selectedCorrespondingEntitiesMap: SelectedCorrespondingEntitiesMap = new Map()

    tableEntities.forEach(tableEntity => {
      selectedCorrespondingEntitiesMap.set(
        tableEntity.mainEntity.id,
        this.getInitialCorrespondingEntitySetsForMainEntity({ tableEntity })
      )
    })
    this.setSelectedCorrespondingEntitiesMap(selectedCorrespondingEntitiesMap)
  }

  private getCorrespondingEntitiesAndCalcStats = (
    stats: ProductProjectStat[],
    initialStatValues?: Partial<StatsOverviewTableEntity['stats']>
  ): {
    primaryCorrespondingEntities: StatsOverviewTableEntity['primaryCorrespondingEntities'],
    primaryCorrespondingEntitiesCount: StatsOverviewTableEntity['primaryCorrespondingEntitiesCount'],
    secondaryCorrespondingEntities: StatsOverviewTableEntity['secondaryCorrespondingEntities'],
    secondaryCorrespondingEntitiesCount: StatsOverviewTableEntity['secondaryCorrespondingEntitiesCount'],
    stats: StatsOverviewTableEntity['stats'],
    bestItemPrimary: PieSegment['bestItem'],
    bestItemSecondary: PieSegment['bestItem']
  } => {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        selectedTab
      }
    } = this.rootStore

    const mainEntityIdKey = getCorrespondingEntityIdKey(
      this.tableSettings.mainEntityColLabel
    )
    const primaryCorrespondingEntityIds: Set<number> = new Set()
    const primaryCorrespondingEntityIdKey = getCorrespondingEntityIdKey(
      this.tableSettings.primaryCorrespondingEntityColLabel
    )
    const secondaryCorrespondingEntityIds: Set<number> = new Set()
    const secondaryCorrespondingEntityIdKey = getCorrespondingEntityIdKey(
      this.tableSettings.secondaryCorrespondingEntityColLabel
    )

    let blobs = initialStatValues?.blobs || 0
    let blobAttached = initialStatValues?.blobAttached || 0
    let blobDetached = initialStatValues?.blobDetached || 0

    let product = initialStatValues?.product || 0
    let productAdded = initialStatValues?.productAdded || 0

    let posters = initialStatValues?.posters || 0
    let posterAttached = initialStatValues?.posterAttached || 0
    let totalPosterAttachedTime = initialStatValues?.totalPosterAttachedTime || 0

    let purchases = initialStatValues?.purchases || 0
    let vov = initialStatValues?.vov || 0
    let addedToCart = initialStatValues?.addedToCart || 0

    let total = blobs + blobAttached + blobDetached + 
    product + productAdded + posters + posterAttached + purchases + vov 
    
    let blobsTotal = blobs + blobAttached + blobDetached
    let productTotal = product
    let posterTotal = posters + posterAttached


    if (selectedTab === OverviewStatTabs.BY_INTERACTION) {
      total += addedToCart
    }

    const bestItemPrimary = {
      id: null,
      total: 0
    }
    const bestItemSecondary = {
      id: null,
      total: 0
    }

    stats
      .filter(stat => {
        const primaryEntityIdKey =
          `${stat[mainEntityIdKey]}-${stat[primaryCorrespondingEntityIdKey]}`
        const secondaryEntityIdKey =
          `${primaryEntityIdKey}-${stat[secondaryCorrespondingEntityIdKey]}`

        if (this.columnFilterSelections.primary.has(primaryEntityIdKey)) {
          return this.columnFilterSelections.secondary.has(secondaryEntityIdKey)
        } else {
          return false
        }
      })
      .forEach(stat => {
        stat[primaryCorrespondingEntityIdKey] &&
          primaryCorrespondingEntityIds.add(stat[primaryCorrespondingEntityIdKey])
        stat[secondaryCorrespondingEntityIdKey] &&
          secondaryCorrespondingEntityIds.add(stat[secondaryCorrespondingEntityIdKey])

        let statTotal = 0
        let blTotal = 0
        let prTotal = 0
        let posTotal = 0


        if (selectedTab === OverviewStatTabs.BY_EXPENSES) {
          statTotal = (stat.blob?.revenue || 0) +
            (stat.blobAttached?.revenue || 0) +
            (stat.blobDetached?.revenue || 0) +
            (stat.productPage?.revenue || 0) +
            (stat.productAdded?.revenue || 0) +
            (stat.poster?.revenue || 0) +
            (stat.posterAttached?.revenue || 0) +
            (stat.commission || 0) +
            (stat.vov?.revenue || 0)

          blTotal = (stat.blob?.revenue || 0) +
          (stat.blobAttached?.revenue || 0) +
          (stat.blobDetached?.revenue || 0)

          productAdded += (stat.productAdded?.revenue || 0)
          prTotal = (stat.productPage?.revenue || 0) +
          (stat.productAdded?.revenue || 0)

          posterAttached += (stat.posterAttached?.revenue || 0)
          posTotal = (stat.poster?.revenue || 0) +
          (stat.posterAttached?.revenue || 0)

          blobs += (stat.blob?.revenue || 0)
          blobAttached += (stat.blobAttached?.revenue || 0)
          blobDetached += (stat.blobDetached?.revenue || 0)
          product += (stat.productPage?.revenue || 0)
          posters += (stat.poster?.revenue || 0)
          purchases += (stat.commission || 0)
          vov += (stat.vov?.revenue || 0)
        } else {
          statTotal = (stat.blob?.eventCount || 0) +
            (stat.blobAttached?.eventCount || 0) +
            (stat.blobDetached?.eventCount || 0) +
            (stat.productPage?.eventCount || 0) +
            (stat.productAdded?.eventCount || 0) +
            (stat.poster?.eventCount || 0) +
            (stat.posterAttached?.eventCount || 0) +
            (stat.count || 0) +
            (stat.vov?.eventCount || 0) +
            (stat.addToCart || 0)

          blTotal = (stat.blob?.eventCount || 0) +
          (stat.blobAttached?.eventCount || 0) +
          (stat.blobDetached?.eventCount || 0) 

          productAdded += (stat.productAdded?.eventCount || 0)
          prTotal = (stat.productPage?.eventCount || 0)
          posterAttached += (stat.posterAttached?.eventCount || 0)
          posTotal = (stat.poster?.eventCount || 0) +
          (stat.posterAttached?.eventCount || 0)

          blobs += (stat.blob?.eventCount || 0)
          blobAttached += (stat.blobAttached?.eventCount || 0)
          blobDetached += (stat.blobDetached?.eventCount || 0)
          product += (stat.productPage?.eventCount || 0)
          posters += (stat.poster?.eventCount || 0)
          purchases += (stat.count || 0)
          vov += (stat.vov?.eventCount || 0)
          addedToCart += (stat.addToCart || 0)
          totalPosterAttachedTime += (stat.posterAttached?.totalPosterAttachedTime || 0)
        }

        total += statTotal
        blobsTotal += blTotal
        productTotal += prTotal
        posterTotal += posTotal

        if (statTotal > bestItemPrimary.total) {
          bestItemPrimary.id = stat[primaryCorrespondingEntityIdKey]
          bestItemPrimary.total = statTotal
        }
        if (statTotal > bestItemSecondary.total) {
          bestItemSecondary.id = stat[secondaryCorrespondingEntityIdKey]
          bestItemSecondary.total = statTotal
        }
      })

    if (selectedTab === OverviewStatTabs.BY_EXPENSES) {
      blobs = getToFixed2Number(blobs)
      blobAttached = getToFixed2Number(blobAttached)
      blobDetached = getToFixed2Number(blobDetached)
      productAdded = getToFixed2Number(productAdded)
      product = getToFixed2Number(product)
      posters = getToFixed2Number(posters)
      posterAttached = getToFixed2Number(posterAttached)
      purchases = getToFixed2Number(purchases)
      vov = getToFixed2Number(vov)
    }

    const primaryCorrespondingEntities = this.getEntities(
      primaryCorrespondingEntityIdKey,
      Array.from(primaryCorrespondingEntityIds.values())
    )
    const primaryCorrespondingEntitiesCount = primaryCorrespondingEntityIds.size
    const secondaryCorrespondingEntities = this.getEntities(
      secondaryCorrespondingEntityIdKey,
      Array.from(secondaryCorrespondingEntityIds.values())
    )
    const secondaryCorrespondingEntitiesCount = secondaryCorrespondingEntityIds.size

    return {
      primaryCorrespondingEntities,
      primaryCorrespondingEntitiesCount,
      secondaryCorrespondingEntities,
      secondaryCorrespondingEntitiesCount,
      stats: {
        blobs,
        blobAttached,
        blobDetached,
        blobsTotal,
        product,
        productAdded,
        productTotal,
        posters,
        posterAttached,
        totalPosterAttachedTime,
        posterTotal,
        purchases,
        vov,
        addedToCart,
        total
      },
      bestItemPrimary: {
        productId: primaryCorrespondingEntityIdKey === 'productId' && bestItemPrimary.id,
        productStatNumber: bestItemPrimary.total,
        isPrice: selectedTab === OverviewStatTabs.BY_EXPENSES,
        showStoreName: true
      },
      bestItemSecondary: {
        productId: secondaryCorrespondingEntityIdKey === 'productId' && bestItemSecondary.id,
        productStatNumber: bestItemSecondary.total,
        isPrice: selectedTab === OverviewStatTabs.BY_EXPENSES,
        showStoreName: true
      }
    }
  }

  clearEntitySelectionsAndTotalPeriodData = () => {
    this.setSelectedMainEntities(new Map())
    this.setSelectedCorrespondingEntitiesMap(new Map())
    this.rootStore[STATISTICS_OVERVIEW_STORE].clearTotalPeriodDataMap()
  }

  tableSortingCompareFn = (a: StatsOverviewTableEntity, b: StatsOverviewTableEntity): number => {
    const {
      sortProp,
      sortDirection
    } = this.pagination

    switch (sortProp) {
      case StatsOverviewTableSortProps.BLOBS: {
        return getSortingResult(a.stats.blobs, b.stats.blobs, sortDirection)
      }
      case StatsOverviewTableSortProps.PRODUCT_PAGES: {
        return getSortingResult(a.stats.product, b.stats.product, sortDirection)
      }
      case StatsOverviewTableSortProps.POSTERS: {
        return getSortingResult(a.stats.posters, b.stats.posters, sortDirection)
      }
      case StatsOverviewTableSortProps.ADDED_TO_CART: {
        return getSortingResult(a.stats.addedToCart, b.stats.addedToCart, sortDirection)
      }
      case StatsOverviewTableSortProps.PURCHASES: {
        return getSortingResult(a.stats.purchases, b.stats.purchases, sortDirection)
      }
      case StatsOverviewTableSortProps.VOV: {
        return getSortingResult(a.stats.vov, b.stats.vov, sortDirection)
      }
      case StatsOverviewTableSortProps.TOTAL: {
        return getSortingResult(a.stats.total, b.stats.total, sortDirection)
      }
      default: return 0
    }
  }

  triggerSearch: TriggerSearchFunc = (options): void => {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        getStatsFor
      }
    } = this.rootStore

    this.setProcessing(true)

    const isEntitySelected = (entity: StatsTableEntityObject): boolean => {
      if (!entity) return false
      const {
        children
      } = entity as ProjectDTO

      const hasChildren = Boolean(children && children.length)

      if (hasChildren) {
        return children.some(isEntitySelected)
      } else {
        return this.columnFilterSelections.main.has(entity.id)
      }
    }

    const parseProjectToTableEntity = (project: ProjectDTO): StatsOverviewTableEntity => {
      const children = (project.children || [])
        .filter(isEntitySelected)
        .map(parseProjectToTableEntity)

      const statsAndCorrespondingEntities = this.getCorrespondingEntitiesAndCalcStats(
        getStatsFor({ projectId: project.id })
      )

      let primaryCorrespondingEntitiesSet = new Set(
        statsAndCorrespondingEntities.primaryCorrespondingEntities || []
      )
      let secondaryCorrespondingEntitiesSet = new Set(
        statsAndCorrespondingEntities.secondaryCorrespondingEntities || []
      )

      children.forEach(tableEntity => {
        primaryCorrespondingEntitiesSet = new Set([
          ...primaryCorrespondingEntitiesSet,
          ...tableEntity.primaryCorrespondingEntities
        ])
        secondaryCorrespondingEntitiesSet = new Set([
          ...secondaryCorrespondingEntitiesSet,
          ...tableEntity.secondaryCorrespondingEntities
        ])
      })

      const primaryCorrespondingEntitiesCount = primaryCorrespondingEntitiesSet.size
      const primaryCorrespondingEntities = [...primaryCorrespondingEntitiesSet.values()]
      const secondaryCorrespondingEntitiesCount = secondaryCorrespondingEntitiesSet.size
      const secondaryCorrespondingEntities = [...secondaryCorrespondingEntitiesSet.values()]

      return {
        mainEntity: project,
        ...statsAndCorrespondingEntities,
        primaryCorrespondingEntities,
        primaryCorrespondingEntitiesCount,
        secondaryCorrespondingEntities,
        secondaryCorrespondingEntitiesCount,
        children
      }
    }

    const parseBrandToTableEntity = (brand: BrandDTO): StatsOverviewTableEntity => {
      return {
        mainEntity: brand,
        ...this.getCorrespondingEntitiesAndCalcStats(getStatsFor({ brandId: brand.id }))
      }
    }

    const parseProductToTableEntity = (product: ProductDTO): StatsOverviewTableEntity => {
      return {
        mainEntity: product,
        ...this.getCorrespondingEntitiesAndCalcStats(getStatsFor({
          productId: product.id
        }))
      }
    }

    const results: StatsOverviewTableEntity[] = (() => {
      switch (this.tableSettings.mainEntityColLabel) {
        case DraggableColLabels.PROJECT: {
          return this.tableProjects
            .filter(isEntitySelected)
            .map(parseProjectToTableEntity)
        }
        case DraggableColLabels.STORE: {
          return this.tableBrands
            .filter(isEntitySelected)
            .map(parseBrandToTableEntity)
        }
        case DraggableColLabels.PRODUCTS: {
          return this.tableProducts
            .filter(isEntitySelected)
            .map(parseProductToTableEntity)
        }
      }
    })()

    const {
      page: currentPagePaginationSetting,
      size
    } = this.pagination

    results.sort(this.tableSortingCompareFn)

    const page = options?.resetPageNumber ? 0 : currentPagePaginationSetting

    this.setPaginationProps({
      page: options?.resetPageNumber ? 0 : page,
      totalElements: results.length,
      totalPages: Math.ceil(results.length / size)
    })
    this.updateSelectedCorrespondingEntities(results)

    const startIndex = page * size
    const endIndex = startIndex + size
    this.setSearchResults(results.slice(startIndex, endIndex))
    this.setProcessing(false)
  }

  triggerPeriodChartUpdate = (mainEntityId?: number): void => {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        getProjectPeriodStats,
        getProductPeriodStats
      }
    } = this.rootStore

    if (this.updatePeriodChartTimeout) {
      clearTimeout(this.updatePeriodChartTimeout)
    }

    const getStats = this.isMainEntityProject
      ? () => mainEntityId
        ? getProjectPeriodStats(this.getParentId(mainEntityId))
        : Promise.all(this.selectedMainEntitiesIds.map(getProjectPeriodStats))
      : () => mainEntityId
        ? getProductPeriodStats(mainEntityId)
        : Promise.all(this.selectedMainEntitiesIds.map(getProductPeriodStats))

    this.updatePeriodChartTimeout = setTimeout(getStats, 300)
  }

  onColumnEntityCheckboxClick = (
    mainEntityId: number,
    correspondingEntityId: number,
    columnType: StatsTableColType,
    options?: {
      forceSelect?: boolean,
      forceDeselect?: boolean
    }
  ): void => {
    // console.log({
    //   mainEntityId,
    //   correspondingEntityId,
    //   columnType
    // })

    const columnLabel = this.getEntityColLabelByColType(columnType)
    const selectedCorrespondingEntitySets: SelectedCorrespondingEntitySets =
      this.getSelectedCorrespondingEntitySets(mainEntityId)
    const isColEntitySelected: boolean = ((): boolean => {
      switch (true) {
        case options?.forceSelect: {
          return false
        }
        case options?.forceDeselect: {
          return true
        }
        default: {
          return this.isCorrespondingEntitySelected(
            mainEntityId,
            correspondingEntityId,
            columnType
          )
        }
      }
    })()

    const initialCorrespondingEntitySets: SelectedCorrespondingEntitySets =
      this.getInitialCorrespondingEntitySetsForMainEntity({ mainEntityId })

    switch (columnLabel) {
      case DraggableColLabels.PROJECT: {
        const {
          [PROJECT_MAIN_STORE]: {
            doesProjectHasChildren,
            getProjectChildrenIds
          },
          [STATISTICS_OVERVIEW_STORE]: {
            getBrandIdsForProject,
            getProductIdsForProject
          }
        } = this.rootStore

        const deleteCorrespondingEntitiesForProject = (projectId: number) => {
          selectedCorrespondingEntitySets[DraggableColLabels.PROJECT]
            .delete(projectId)
          const selectedProjectIds = Array
            .from(selectedCorrespondingEntitySets[DraggableColLabels.PROJECT])
          getBrandIdsForProject(projectId).forEach(brandId => {
            const brandIsAttachedToAnotherProject = selectedProjectIds
              .some(id => Array.from(getBrandIdsForProject(id)).some(id => id === brandId))
            if (!brandIsAttachedToAnotherProject) {
              selectedCorrespondingEntitySets[DraggableColLabels.STORE].delete(brandId)
            }
          })
          getProductIdsForProject(projectId).forEach(productId => {
            const productIsAttachedToAnotherProject = selectedProjectIds
              .some(id => Array.from(getProductIdsForProject(id)).some(id => id === productId))
            if (!productIsAttachedToAnotherProject) {
              selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].delete(productId)
            }
          })
        }

        const addCorrespondingEntitiesForProject = (projectId: number) => {
          selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].add(projectId)
          getBrandIdsForProject(projectId).forEach(brandId => {
            if (initialCorrespondingEntitySets[DraggableColLabels.STORE].has(brandId)) {
              selectedCorrespondingEntitySets[DraggableColLabels.STORE].add(brandId)
            }
          })
          getProductIdsForProject(projectId).forEach(productId => {
            if (initialCorrespondingEntitySets[DraggableColLabels.PRODUCTS].has(productId)) {
              selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].add(productId)
            }
          })
        }

        const hasChildren = doesProjectHasChildren(correspondingEntityId)
        const childrenIds = getProjectChildrenIds(correspondingEntityId)

        if (isColEntitySelected) {
          if (hasChildren) {
            childrenIds.forEach(deleteCorrespondingEntitiesForProject)
          } else {
            deleteCorrespondingEntitiesForProject(correspondingEntityId)
          }
        } else {
          if (hasChildren) {
            childrenIds.forEach(addCorrespondingEntitiesForProject)
          } else {
            addCorrespondingEntitiesForProject(correspondingEntityId)
          }
        }

        break
      }
      case DraggableColLabels.STORE: {
        const {
          [STATISTICS_OVERVIEW_STORE]: {
            getProjectIdsForBrand,
            getBrandIdsForProject,
            getProductIdsForBrand
          }
        } = this.rootStore
        const productIds = getProductIdsForBrand(correspondingEntityId)
        const projectIds = getProjectIdsForBrand(correspondingEntityId)

        if (isColEntitySelected) {
          // apply to Brands/Stores selection
          selectedCorrespondingEntitySets[DraggableColLabels.STORE]
            .delete(correspondingEntityId)

          // apply to Projects selection
          const hasCorrespondingProjects =
            Boolean(selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].size)
          if (hasCorrespondingProjects) {
            projectIds.forEach(projectId => {
              if (
                selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].has(projectId)
              ) {
                const projectHasAnotherBrandAttached = Array
                  .from(getBrandIdsForProject(projectId))
                  .some(brandId =>
                    selectedCorrespondingEntitySets[DraggableColLabels.STORE].has(brandId)
                  )
                if (!projectHasAnotherBrandAttached) {
                  selectedCorrespondingEntitySets[DraggableColLabels.PROJECT]
                    .delete(projectId)
                }
              }
            })
          }

          // apply to Products selection
          productIds.forEach(productId => {
            selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].delete(productId)
          })
        } else {
          // apply to Brands/Stores selection
          selectedCorrespondingEntitySets[DraggableColLabels.STORE]
            .add(correspondingEntityId)

          // apply to Products selection
          productIds.forEach(productId => {
            if (initialCorrespondingEntitySets[DraggableColLabels.PRODUCTS].has(productId)) {
              selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].add(productId)
            }
          })

          // apply to Projects selection
          projectIds.forEach(projectId => {
            if (initialCorrespondingEntitySets[DraggableColLabels.PROJECT].has(projectId)) {
              selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].add(projectId)
            }
          })
        }
        break
      }
      case DraggableColLabels.PRODUCTS: {
        const {
          [PRODUCTS_MAIN_STORE]: {
            getProductBrandId
          },
          [STATISTICS_OVERVIEW_STORE]: {
            getProductIdsForBrand,
            getProjectIdsForProduct,
            getProductIdsForProject
          }
        } = this.rootStore
        const brandId = getProductBrandId(correspondingEntityId)
        const projectIds = getProjectIdsForProduct(correspondingEntityId)

        if (isColEntitySelected) {
          // apply to Products selection
          selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS]
            .delete(correspondingEntityId)

          // apply to Brands/Stores selection
          const brandHasAnotherSelectedProduct = Array.from(getProductIdsForBrand(brandId))
            .some(productId =>
              selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].has(productId)
            )
          if (!brandHasAnotherSelectedProduct) {
            selectedCorrespondingEntitySets[DraggableColLabels.STORE].delete(brandId)
          }

          // apply to Projects selection
          projectIds.forEach(projectId => {
            const isProjectSelected =
              selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].has(projectId)
            if (isProjectSelected) {
              const projectHasAnotherProductAttached = Array
                .from(getProductIdsForProject(projectId))
                .some(productId =>
                  selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS].has(productId)
                )
              if (!projectHasAnotherProductAttached) {
                selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].delete(projectId)
              }
            }
          })
        } else {
          // apply to Products selection
          selectedCorrespondingEntitySets[DraggableColLabels.PRODUCTS]
            .add(correspondingEntityId)

          // apply to Brands/Store selection
          selectedCorrespondingEntitySets[DraggableColLabels.STORE]
            .add(brandId)

          // apply to Projects selection
          projectIds.forEach(projectId => {
            const isProjectACorrespondingEntity =
              initialCorrespondingEntitySets[DraggableColLabels.PROJECT].has(projectId)
            if (isProjectACorrespondingEntity) {
              selectedCorrespondingEntitySets[DraggableColLabels.PROJECT].add(projectId)
            }
          })
        }
        break
      }
      default:
        break
    }

    this.setSelectedCorrespondingEntities(mainEntityId, selectedCorrespondingEntitySets)
    if (this.isMainEntitySelected(mainEntityId)) {
      this.triggerPeriodChartUpdate(mainEntityId)
    }
  }

  onColumnEntityAggregationCheckboxClick = (
    mainEntityId: number,
    correspondingEntityIds: number[],
    columnType: StatsTableColType
  ): void => {
    const isSomeCorrespondingEntitySelected: boolean = this.isSomeCorrespondingEntitySelected(
      mainEntityId,
      correspondingEntityIds,
      columnType
    )

    correspondingEntityIds.forEach(id => {
      this.onColumnEntityCheckboxClick(
        mainEntityId,
        id,
        columnType,
        {
          forceSelect: !isSomeCorrespondingEntitySelected,
          forceDeselect: isSomeCorrespondingEntitySelected
        }
      )
    })
  }

  onEventTypeShowCheckboxClick = (eventType: ViewerEventEnums): void => {
    this.switchViewerEventShown(eventType)
    this.triggerPeriodChartUpdate()
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        getBestProducts
      }
    } = this.rootStore
    if (this.isMainEntityProduct && this.selectedMainEntitiesIds &&
      this.selectedMainEntitiesIds.length) {
      getBestProducts()
    }
  }

  refreshColumnFilterSelections = (): void => {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        statsBrandIds,
        statsProjectIds,
        statsProductIds,
        getProjectIdsForBrand,
        getProjectIdsForProduct,
        getProductIdsForBrand,
        getProductIdsForProject,
        getBrandIdsForProject,
        getBrandIdsForProduct
      }
    } = this.rootStore
    const {
      mainEntityColLabel,
      primaryCorrespondingEntityColLabel
    } = this.tableSettings

    let mainEntityIds: number[] = []
    const primaryEntityIdKeys: string[] = []
    const secondaryEntityIdKeys: string[] = []

    const feedEntityIdKeys = (args: {
      getPrimaryEntityIds: (_: number) => Set<number>
      getSecondaryEntityIds: (_: number) => Set<number>
      getSecondaryEntityIdByMainEntityId?: (_: number) => number
      options?: {
        skipPrimaryEntityFiltering?: boolean
        skipSecondaryEntityFiltering?: boolean
      }
    }) => {
      const {
        getPrimaryEntityIds,
        getSecondaryEntityIds,
        getSecondaryEntityIdByMainEntityId =
        this.rootStore[PRODUCTS_MAIN_STORE].getProductBrandId,
        options
      } = args

      mainEntityIds.forEach(mainEntityId => {
        getPrimaryEntityIds(mainEntityId).forEach(primaryEntityId => {
          primaryEntityIdKeys.push(`${mainEntityId}-${primaryEntityId}`)
          if (options?.skipSecondaryEntityFiltering) {
            secondaryEntityIdKeys
              .push(`${mainEntityId}-${primaryEntityId
                }-${getSecondaryEntityIdByMainEntityId(mainEntityId)}`)
          } else {
            getSecondaryEntityIds(
              options?.skipPrimaryEntityFiltering
                ? mainEntityId
                : primaryEntityId
            ).forEach(secondaryEntityId => {
              secondaryEntityIdKeys
                .push(`${mainEntityId}-${primaryEntityId}-${secondaryEntityId}`)
            })
          }
        })
      })
    }

    switch (mainEntityColLabel) {
      case DraggableColLabels.STORE: {
        mainEntityIds = statsBrandIds

        switch (primaryCorrespondingEntityColLabel) {
          case DraggableColLabels.PROJECT: {
            feedEntityIdKeys({
              getPrimaryEntityIds: getProjectIdsForBrand,
              getSecondaryEntityIds: getProductIdsForProject
            })
            break
          }
          case DraggableColLabels.PRODUCTS: {
            feedEntityIdKeys({
              getPrimaryEntityIds: getProductIdsForBrand,
              getSecondaryEntityIds: getProjectIdsForProduct
            })
            break
          }
          default: break
        }
        break
      }
      case DraggableColLabels.PROJECT: {
        mainEntityIds = statsProjectIds

        switch (primaryCorrespondingEntityColLabel) {
          case DraggableColLabels.STORE: {
            feedEntityIdKeys({
              getPrimaryEntityIds: getBrandIdsForProject,
              getSecondaryEntityIds: getProductIdsForBrand
            })
            break
          }
          case DraggableColLabels.PRODUCTS: {
            feedEntityIdKeys({
              getPrimaryEntityIds: getProductIdsForProject,
              getSecondaryEntityIds: getBrandIdsForProduct
            })
            break
          }
          default: break
        }
        break
      }
      case DraggableColLabels.PRODUCTS: {
        mainEntityIds = statsProductIds

        switch (primaryCorrespondingEntityColLabel) {
          case DraggableColLabels.STORE: {
            feedEntityIdKeys({
              getPrimaryEntityIds: getBrandIdsForProduct,
              getSecondaryEntityIds: getProjectIdsForProduct,
              options: {
                skipPrimaryEntityFiltering: true
              }
            })
            break
          }
          case DraggableColLabels.PROJECT: {
            feedEntityIdKeys({
              getPrimaryEntityIds: getProjectIdsForProduct,
              getSecondaryEntityIds: getProductIdsForProject,
              options: {
                skipSecondaryEntityFiltering: true
              }
            })
            break
          }
          default: break
        }
        break
      }
      default: break
    }

    this.setColumnFilterSelections({
      main: new Set(mainEntityIds),
      mainFull: new Set(mainEntityIds),
      primary: new Set(primaryEntityIdKeys),
      primaryFull: new Set(primaryEntityIdKeys),
      secondary: new Set(secondaryEntityIdKeys),
      secondaryFull: new Set(secondaryEntityIdKeys)
    })
  }

  get selectedMainEntitiesIds (): number[] {
    // TODO: expect if main entity is project and apply several sets of ids if selected project has children
    return [...this.selectedMainEntities.keys()]
  }

  get selectedViewerEventsToShowArr (): ViewerEventEnums[] {
    return Array.from(this.selectedViewerEventsToShow.values())
  }

  get tableProjects (): ProjectDTO[] {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        statsProjectIds
      }
    } = this.rootStore

    const idsSet = new Set(statsProjectIds)
    return Array.from(this.rootStore[PROJECT_MAIN_STORE].projectsHierarchyList
      .filter(project => hasMatchingChild(project, idsSet)))
    // return this.rootStore[PROJECT_MAIN_STORE].projectsHierarchyList
  }

  get tableBrands (): BrandDTO[] {
    const {
      [STATISTICS_PAGE_STORE]: {
        selectedStoreIds
      },
      [STATISTICS_OVERVIEW_STORE]: {
        statsBrandIds
      }
    } = this.rootStore

    const brandIds = selectedStoreIds.length
      ? statsBrandIds.filter(id => selectedStoreIds.includes(id))
      : statsBrandIds
    return this.getEntities('brandId', brandIds) as BrandDTO[]
  }

  get tableProducts (): ProductDTO[] {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        statsProductIds
      }
    } = this.rootStore

    return this.getEntities('productId', statsProductIds) as ProductDTO[]
  }

  @observable getSubTableEntity = computedFn(
    (
      mainEntity: StatsTableEntityObject,
      correspondingEntity: StatsTableEntityObject,
      correspondingEntityType: StatsTableColType
    ): StatsOverviewTableEntity => {
      const correspondingEntityColLabel =
        this.getEntityColLabelByColType(correspondingEntityType)
      const correspondingEntityIdKey =
        getCorrespondingEntityIdKey(correspondingEntityColLabel)
      const mainEntityIdKey =
        getCorrespondingEntityIdKey(this.tableSettings.mainEntityColLabel)

      const {
        getStatsFor
      } = this.rootStore[STATISTICS_OVERVIEW_STORE]

      return {
        mainEntity: mainEntity,
        ...this.getCorrespondingEntitiesAndCalcStats(getStatsFor({
          [mainEntityIdKey]: mainEntity.id,
          [correspondingEntityIdKey]: correspondingEntity.id
        })),
        children: []
      }
    }
  )

  @observable isViewerEventShown = computedFn(
    (eventType: ViewerEventEnums): boolean => {
      return this.selectedViewerEventsToShow.has(eventType)
    }
  )

  @observable isEventTypeShowCheckboxDisabled = computedFn(
    (eventType: ViewerEventEnums): boolean => {
      return this.isViewerEventShown(eventType) && this.selectedViewerEventsToShow.size === 1
    }
  )

  @observable getFilterPropsByColLabel = computedFn(
    (colType: StatsTableColType): SelectTreeViewProps => {
      const {
        mainEntityColLabel,
        primaryCorrespondingEntityColLabel
      } = this.tableSettings

      const label = this.getEntityColLabelByColType(colType)

      let options: SelectTreeViewProps['options'] = []
      const entityIdKeysForMainEntity = new Set<number | string>()

      // let dropdownDisabled: boolean = false

      if (colType === StatsTableColType.MAIN) {
        // dropdownDisabled = this.columnFilterSelections.main.size < 2
        switch (label) {
          case DraggableColLabels.STORE: {
            options = this.tableBrands.map(brand => {
              entityIdKeysForMainEntity.add(brand.id)

              return {
                label: brand.name,
                isSelected: this.isFilterEntitySelected(colType, brand.id),
                onClick: () => this.onFilterEntityCheckboxClick(colType, brand.id)
              }
            })

            break
          }
          case DraggableColLabels.PROJECT: {
            const parseProjectsToOptions = (projects: ProjectDTO[]): SelectTreeViewProps['options'] => {
              if (!projects) return []

              return projects.map(project => {
                const hasChildren = Boolean(project.children && project.children.length)
                const children: SelectTreeViewProps['options'] = hasChildren
                  ? parseProjectsToOptions(project.children)
                  : null
                const isSelected = hasChildren
                  ? !children.some(childOption => !childOption.isSelected)
                  : this.columnFilterSelections[colType]?.has(project.id)
                const onClick = (): void => {
                  if (hasChildren) {
                    children.forEach(childOption =>
                      childOption.onClick && childOption.onClick()
                    )
                  } else {
                    this.onFilterEntityCheckboxClick(colType, project.id)
                  }
                }

                !hasChildren && entityIdKeysForMainEntity.add(project.id)

                return {
                  label: project.name,
                  isExpandable: hasChildren,
                  isSelected,
                  children,
                  onClick
                }
              })
            }

            options = parseProjectsToOptions(this.tableProjects)
            break
          }
          case DraggableColLabels.PRODUCTS: {
            options = this.tableProducts.map(product => {
              entityIdKeysForMainEntity.add(product.id)

              return {
                label: product.name,
                image: getMainImageSrcForObj(product),
                isSelected: this.isFilterEntitySelected(colType, product.id),
                onClick: () => this.onFilterEntityCheckboxClick(colType, product.id)
              }
            })
            break
          }
          default: break
        }
      }

      const {
        [STATISTICS_OVERVIEW_STORE]: {
          getProjectIdsForBrand,
          getProjectIdsForProduct,
          getProductIdsForProject,
          getProductIdsForBrand,
          getBrandIdsForProject,
          getBrandIdsForProduct
        },
        [PROJECT_MAIN_STORE]: {
          getProjectById
        },
        [PRODUCTS_MAIN_STORE]: {
          getProductById
        },
        [BRANDS_MAIN_STORE]: {
          getBrandById
        }
      } = this.rootStore

      let mainEntities: StatsTableEntityObject[] = []
      let getPrimaryEntityIds = (_: number) => new Set<number>()
      let getPrimaryEntityById = (_: number): StatsTableEntityObject => null

      const filterOptionSettings: FilterOptionSettings = {}

      if (colType === StatsTableColType.PRIMARY) {
        // dropdownDisabled = this.columnFilterSelections.main.size < 2
        //   ? false
        //   : this.columnFilterSelections.main.size ===
        //   this.columnFilterSelections.mainFull.size
        switch (mainEntityColLabel) {
          case DraggableColLabels.STORE: {
            mainEntities = this.tableBrands
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.PROJECT: {
                getPrimaryEntityIds = getProjectIdsForBrand
                getPrimaryEntityById = getProjectById
                break
              }
              case DraggableColLabels.PRODUCTS: {
                getPrimaryEntityIds = getProductIdsForBrand
                getPrimaryEntityById = getProductById
                filterOptionSettings.showImageForPrimary = true
                break
              }
              default: break
            }
            break
          }
          case DraggableColLabels.PROJECT: {
            mainEntities = this.tableProjects
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.STORE: {
                getPrimaryEntityIds = getBrandIdsForProject
                getPrimaryEntityById = getBrandById
                break
              }
              case DraggableColLabels.PRODUCTS: {
                getPrimaryEntityIds = getProductIdsForProject
                getPrimaryEntityById = getProductById
                filterOptionSettings.showImageForPrimary = true
                break
              }
              default: break
            }
            break
          }
          case DraggableColLabels.PRODUCTS: {
            mainEntities = this.tableProducts
            filterOptionSettings.showImageForMain = true
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.PROJECT: {
                getPrimaryEntityIds = getProjectIdsForProduct
                getPrimaryEntityById = getProjectById
                break
              }
              case DraggableColLabels.STORE: {
                mainEntities = []
                // this filter combination makes no sense so no filter options should be rendered
                // getPrimaryEntityIds = getBrandIdsForProduct
                // getPrimaryEntityById = getBrandById
                break
              }
              default: break
            }
            break
          }
          default: break
        }

        options = this.getFilterOptionsForPrimaryCorrespondingEntitiesColumn({
          entityIdKeysForMainEntity,
          getPrimaryEntityById,
          getPrimaryEntityIds,
          mainEntities,
          settings: filterOptionSettings
        })
      }

      if (colType === StatsTableColType.SECONDARY) {
        // dropdownDisabled = this.columnFilterSelections.primary.size < 2
        //   ? false
        //   : this.columnFilterSelections.primary.size ===
        //   this.columnFilterSelections.primaryFull.size
        // console.log({ dropdownDisabled })
        // console.log('this.columnFilterSelections.primary.size', this.columnFilterSelections.primary.size)
        let getSecondaryEntityIds = (_: number) => new Set<number>()
        let getSecondaryEntityById = (_: number): StatsTableEntityObject => null

        switch (mainEntityColLabel) {
          case DraggableColLabels.STORE: {
            mainEntities = this.tableBrands
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.PROJECT: {
                getPrimaryEntityIds = getProjectIdsForBrand
                getPrimaryEntityById = getProjectById
                getSecondaryEntityIds = getProductIdsForProject
                getSecondaryEntityById = getProductById
                filterOptionSettings.showImageForSecondary = true
                break
              }
              case DraggableColLabels.PRODUCTS: {
                getPrimaryEntityIds = getProductIdsForBrand
                getPrimaryEntityById = getProductById
                getSecondaryEntityIds = getProjectIdsForProduct
                getSecondaryEntityById = getProjectById
                filterOptionSettings.showImageForPrimary = true
                break
              }
              default: break
            }
            break
          }
          case DraggableColLabels.PROJECT: {
            mainEntities = this.tableProjects
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.STORE: {
                getPrimaryEntityIds = getBrandIdsForProject
                getPrimaryEntityById = getBrandById
                getSecondaryEntityIds = getProductIdsForBrand
                getSecondaryEntityById = getProductById
                filterOptionSettings.showImageForSecondary = true
                break
              }
              case DraggableColLabels.PRODUCTS: {
                mainEntities = []
                // getPrimaryEntityIds = getProductIdsForProject
                // getPrimaryEntityById = getProductById

                // // filtering secondaryCorrespondingCol makes no sense in this combination
                // getSecondaryEntityIds = getBrandIdsForProduct
                // getSecondaryEntityById = getBrandById

                filterOptionSettings.showImageForPrimary = true
                break
              }
              default: break
            }
            break
          }
          case DraggableColLabels.PRODUCTS: {
            mainEntities = this.tableProducts
            filterOptionSettings.showImageForMain = true
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.STORE: {
                // no-sense combination
                getPrimaryEntityIds = getBrandIdsForProduct
                getPrimaryEntityById = getBrandById
                getSecondaryEntityIds = getProjectIdsForProduct
                getSecondaryEntityById = getProjectById
                filterOptionSettings.skipPrimaryEntityGrouping = true
                break
              }
              case DraggableColLabels.PROJECT: {
                mainEntities = []
                // getPrimaryEntityIds = getProjectIdsForProduct
                // getPrimaryEntityById = getProjectById
                // getSecondaryEntityIds = getBrandIdsForProject
                // getSecondaryEntityById = getBrandById
                break
              }
              default: break
            }
            break
          }
          default: break
        }

        options = this.getFilterOptionsForSecondaryCorrespondingEntitiesColumn({
          entityIdKeysForMainEntity,
          getPrimaryEntityById,
          getPrimaryEntityIds,
          getSecondaryEntityById,
          getSecondaryEntityIds,
          mainEntities,
          settings: filterOptionSettings
        })
      }

      let optionsCount = 0
      let selectedOptionsCount = 0
      const feedOptionCounters = (opts: TreeViewDropdownLineProps[]) => {
        opts.forEach(option => {
          const hasChildren = Boolean(option.children && option.children.length)
          if (hasChildren) {
            feedOptionCounters(option.children)
          } else {
            optionsCount++
            option.isSelected && selectedOptionsCount++
          }
        })
      }
      feedOptionCounters(options)

      const areAllSelected = optionsCount === selectedOptionsCount
      const selectAllSecondaryLabelText = areAllSelected
        ? `(${optionsCount})`
        : `(${selectedOptionsCount}/${optionsCount})`
      const selectAllOptionProps: SelectTreeViewProps['selectAllOptionProps'] = {
        label: 'Select all',
        secondaryLabelText: selectAllSecondaryLabelText,
        isSelected: areAllSelected,
        onClick: () => this.onFilterAggregationLineCheckboxClick(
          colType,
          [...entityIdKeysForMainEntity as any],
          areAllSelected ? 'deselect' : 'select'
        )
      }

      return {
        // disabled: dropdownDisabled,
        selectAllOptionProps,
        options,
        onDropdownCollapse: () => {
          this.clearEntitySelectionsAndTotalPeriodData()
          this.triggerSearch({ resetPageNumber: true })
        }
      }
    }
  )

  getEventLabel = (eventType: ViewerEventEnums): string => {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        selectedTab
      }
    } = this.rootStore

    switch (eventType) {
      case ViewerEventEnums.BLOB_TRIGGERED: {
        return selectedTab === OverviewStatTabs.BY_EXPENSES ? 'Blobs' : 'Blobs triggered'
      }
      case ViewerEventEnums.PRODUCT_PAGE_OPENED: {
        return selectedTab === OverviewStatTabs.BY_EXPENSES ? 'Product pages' : 'Product pages opened'
      }
      case ViewerEventEnums.POSTER_CLICKED: {
        return selectedTab === OverviewStatTabs.BY_EXPENSES ? 'Posters' : 'Posters clicked'
      }
      case ViewerEventEnums.PURCHASE: {
        return 'Purchases'
      }
      case ViewerEventEnums.VOV_CLICKED: {
        return selectedTab === OverviewStatTabs.BY_EXPENSES ? 'VoV' : 'Vov clicked'
      }
      case ViewerEventEnums.ADD_TO_CART: {
        return 'Added to cart'
      }
      default:
        return ''
    }
  }

  onStatisticsOverviewTabChange = (selectedTab: OverviewStatTabs): void => {
    this.triggerSearch({ resetPageNumber: true })
    if ((selectedTab === OverviewStatTabs.BY_EXPENSES) || (selectedTab === OverviewStatTabs.BY_REVENUE)) {
      this.setSelectedViewerEventsToShow(statsOverviewExpensesEvents)
    } else {
      this.setSelectedViewerEventsToShow(statsOverviewInteractionEvents)
    }
  }

  @action onOverviewPeriodFilterChange = (): void => {
    this.selectedMainEntitiesIds.length = 0
    this.selectedMainEntities.clear()
  }

  setSingleStoreView () {
    this.setTableSetting({
      draggableColumnsCount: 2,
      mainEntityColLabel: DraggableColLabels.PROJECT,
      primaryCorrespondingEntityColLabel: DraggableColLabels.PRODUCTS,
      secondaryCorrespondingEntityColLabel: DraggableColLabels.STORE
    })
    this.init()
    this.rootStore[STATISTICS_OVERVIEW_STORE].clearTotalPeriodDataMap()
  }

  setMultiStoreView () {
    this.setTableSetting({
      draggableColumnsCount: 3,
      mainEntityColLabel: DraggableColLabels.STORE,
      primaryCorrespondingEntityColLabel: DraggableColLabels.PROJECT,
      secondaryCorrespondingEntityColLabel: DraggableColLabels.PRODUCTS
    })
    this.init()
    this.rootStore[STATISTICS_OVERVIEW_STORE].clearTotalPeriodDataMap()
  }
}
